import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, BookMarked, ShoppingBag } from "lucide-react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import BuyBookDialog from "@/components/BuyBookDialog";
import AddToCartButton from "@/components/AddToCartButton";

const FALLBACK = "https://images.pexels.com/photos/11911071/pexels-photo-11911071.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PAGE_SIZE = 20;

export default function Books() {
  const [books, setBooks] = useState([]);
  const [displayedTitles, setDisplayedTitles] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data)).finally(() => setLoading(false));
    api.get("/books/count").then((r) => setDisplayedTitles(r.data.displayed ?? r.data.total)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(books.map((b) => b.category).filter(Boolean)))], [books]);

  const filtered = books.filter((b) => {
    const matchQ = q.trim() === "" || (`${b.title} ${b.author}`.toLowerCase().includes(q.toLowerCase()));
    const matchC = cat === "All" || b.category === cat;
    return matchQ && matchC;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const goToPage = (p) => {
    setPage(Math.min(Math.max(1, p), totalPages));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="books-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="grid grid-cols-12 gap-6 items-end mb-14">
        <div className="col-span-12 md:col-span-8">
          <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-3">Catalogue</div>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#0F172A] tracking-tight leading-[0.95]">
            All Books
          </h1>
          <p className="mt-4 text-neutral-600 max-w-lg text-[15px]">
            Browse every title available at the depot. Filter by subject or search for a specific book.
          </p>
          {!loading && (
            <div data-testid="books-total-count" className="mt-5 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-neutral-500">
                <span className="w-8 h-px bg-[#E05A47]"/>
                <span className="text-[#0F172A] font-medium">
                  {displayedTitles !== null ? displayedTitles.toLocaleString() : books.length}
                </span> Titles in Stock
                {q || cat !== "All" ? (
                  <span className="text-neutral-400">· showing {filtered.length}</span>
                ) : null}
              </div>
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="books-search-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search title or author..."
              className="pl-9 rounded-full border-neutral-200 h-11 bg-white"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => { setCat(c); setPage(1); }}
            data-testid={`filter-${c}`}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors ${
              cat === c
                ? "bg-[#0F172A] text-white border-[#0F172A]"
                : "bg-white text-neutral-700 border-neutral-200 hover:border-[#0F172A]"
            }`}
            style={{ transitionProperty: "background-color, color, border-color" }}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading books...</div>
      ) : filtered.length === 0 ? (
        <div data-testid="no-books" className="rounded-2xl border border-dashed border-neutral-300 p-16 text-center">
          <BookMarked size={28} className="mx-auto text-neutral-400" />
          <p className="mt-4 text-neutral-500">No books found. Try a different search or filter.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
          {pageItems.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              data-testid={`book-item-${b.id}`}
            >
              <div className="aspect-[3/4] rounded-xl overflow-hidden border border-neutral-200 bg-neutral-100 card-hover">
                <img
                  src={b.cover_path ? `${API}/files/book-cover/${b.id}` : FALLBACK}
                  alt={b.title}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = FALLBACK; }}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{b.category}</div>
                <div className="font-serif text-xl text-[#0F172A] mt-1 leading-snug">{b.title}</div>
                <div className="text-sm text-neutral-500">{b.author}</div>
                {b.description && (
                  <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{b.description}</p>
                )}
                <div className="mt-3 text-[#E05A47] font-medium">₹ {Number(b.price).toFixed(0)}</div>
                <div className="mt-4 flex gap-2">
                  <BuyBookDialog book={b} triggerLabel="Buy" triggerClass="flex-1" size="sm"/>
                  <AddToCartButton book={b} size="sm" className="flex-1"/>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {totalPages > 1 && (
          <div data-testid="books-pagination" className="mt-14 flex items-center justify-center gap-4 flex-wrap">
            <button
              data-testid="books-prev-page"
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage === 1}
              className="h-10 px-5 rounded-full text-xs uppercase tracking-[0.15em] border border-neutral-200 bg-white text-neutral-700 hover:border-[#0F172A] disabled:opacity-40 disabled:hover:border-neutral-200 transition-colors"
              style={{ transitionProperty: "border-color" }}
            >
              ← Prev
            </button>
            <span data-testid="books-page-info" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
              Page {safePage} of {totalPages}
            </span>
            <button
              data-testid="books-next-page"
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage === totalPages}
              className="h-10 px-5 rounded-full text-xs uppercase tracking-[0.15em] border border-neutral-200 bg-white text-neutral-700 hover:border-[#0F172A] disabled:opacity-40 disabled:hover:border-neutral-200 transition-colors"
              style={{ transitionProperty: "border-color" }}
            >
              Next →
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
